import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getFileUrl, getPublicAuthorPhotoUrl, libraryAPI } from "@/services/api";
import {
  clearLegacyLibraryDownloads,
  getOfflineManifest,
  syncOfflineManifest,
} from "@/services/libraryDownloadManager";
import {
  downloadSecureLibraryItem,
  clearSecureLibraryTemporaryMedia,
  getSecureLibraryIdentity,
  isSecureOfflineLibraryAvailable,
  listSecureLibraryDownloads,
  prepareSecureLibraryMedia,
  readSecureLibraryItem,
  removeSecureLibraryItem,
} from "@/services/secureLibrary";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  Film,
  Headphones,
  Heart,
  LayoutGrid,
  Library,
  List,
  Maximize2,
  Moon,
  Pause,
  Play,
  RotateCw,
  Search,
  Star,
  Tags,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "sonner";
import articoLogo from "@/assets/artico-logo.png";
import { getProductCategoryLabel } from "@/utils/productCategories";

const themes = {
  royal: {
    label: "Royal",
    shell: "bg-[#FCFBF8] text-[#1A1D1C]",
    hero: "from-[#2A4B46] via-[#315f58] to-[#D4A373]",
    panel: "bg-white border-[#E6E4E0]",
    text: "text-[#2A4B46]",
    muted: "text-[#5C625F]",
  },
  night: {
    label: "Nuit",
    shell: "bg-[#080D0F] text-white",
    hero: "from-[#050708] via-[#13201e] to-[#2A4B46]",
    panel: "bg-[#111819] border-white/10",
    text: "text-white",
    muted: "text-white/65",
  },
  sepia: {
    label: "Sépia",
    shell: "bg-[#F4ECD8] text-[#2F2418]",
    hero: "from-[#5A4632] via-[#8A6A48] to-[#D4A373]",
    panel: "bg-[#FFF8E8] border-[#E4D2B2]",
    text: "text-[#3D2F22]",
    muted: "text-[#7A6349]",
  },
};

const sections = [
  { key: "favorites", title: "Mes Favoris", icon: Star, filter: (item) => item.is_favorite },
  { key: "pdf", title: "Livres & Enseignements", icon: BookOpen, filter: (item) => item.media_type === "pdf" },
  { key: "audio", title: "Fichiers audio", icon: Headphones, filter: (item) => item.media_type === "audio" },
  { key: "video", title: "Fichiers video", icon: Film, filter: (item) => item.media_type === "video" },
];

const libraryViews = {
  premium: { label: "Premium streaming", icon: LayoutGrid },
  collection: { label: "Collection personnelle", icon: Tags },
  streaming: { label: "Netflix", icon: List },
};

function actionLabel(item) {
  const progress = item.progress || {};
  if (Number(progress.percent || 0) > 1 && Number(progress.percent || 0) < 95) {
    if (item.media_type === "pdf" && progress.current_page) return `Reprendre page ${progress.current_page}`;
    if (progress.position_seconds) return `Reprendre à ${formatTime(progress.position_seconds)}`;
    return "Reprendre";
  }
  if (item.media_type === "audio") return "Écouter";
  if (item.media_type === "video") return "Regarder";
  return "Lire";
}

function formatTime(seconds = 0) {
  const safe = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}m${String(secs).padStart(2, "0")}s`;
}

function MediaIcon({ type, className = "" }) {
  const Icon = type === "audio" ? Headphones : type === "video" ? Film : BookOpen;
  return <Icon className={className} />;
}

export default function ArticoLibraryPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [streamUrl, setStreamUrl] = useState("");
  const [streamUrlOrigin, setStreamUrlOrigin] = useState("remote");
  const [loadingStream, setLoadingStream] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfUrlOrigin, setPdfUrlOrigin] = useState("remote");
  const [localPdfData, setLocalPdfData] = useState(null);
  const [downloadedItems, setDownloadedItems] = useState({});
  const [downloadingId, setDownloadingId] = useState("");
  const [offlineManifest, setOfflineManifest] = useState(() => getOfflineManifest());
  const [syncingOfflineManifest, setSyncingOfflineManifest] = useState(false);
  const [search, setSearch] = useState("");
  const [themeName, setThemeName] = useState(() => localStorage.getItem("artico_library_theme") || "royal");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("artico_library_view") || "premium");
  const mediaRef = useRef(null);
  const theme = themes[themeName] || themes.royal;

  const loadLibrary = useCallback(async () => {
    const res = await libraryAPI.list();
    setItems(res.data.items || []);
  }, []);

  const refreshDownloads = useCallback(async () => {
    const downloads = isSecureOfflineLibraryAvailable() ? await listSecureLibraryDownloads() : [];
    const map = downloads.reduce((acc, entry) => {
      acc[entry.product_id] = entry;
      return acc;
    }, {});
    setDownloadedItems(map);
  }, []);

  const refreshOfflineManifest = useCallback(async () => {
    setSyncingOfflineManifest(true);
    try {
      const response = await libraryAPI.sync();
      const manifest = await syncOfflineManifest(response.data, JSON.parse(localStorage.getItem("mmj_user") || "{}")?.id || null);
      setOfflineManifest(manifest);
      const allowed = new Set(manifest.authorized_product_ids || []);
      const localDownloads = isSecureOfflineLibraryAvailable() ? await listSecureLibraryDownloads() : [];
      const revoked = localDownloads.filter((entry) => entry?.product_id && !allowed.has(entry.product_id));
      if (revoked.length) {
        await Promise.all(revoked.map((entry) => removeSecureLibraryItem(entry.product_id)));
        await refreshDownloads();
      }
      return manifest;
    } catch (error) {
      const cached = getOfflineManifest();
      if (cached) setOfflineManifest(cached);
      return cached;
    } finally {
      setSyncingOfflineManifest(false);
    }
  }, [refreshDownloads]);

  useEffect(() => {
    loadLibrary()
      .catch(() => toast.error("Bibliothèque indisponible"));
    refreshDownloads().catch(() => {});
    refreshOfflineManifest().catch(() => {});
  }, [loadLibrary, refreshDownloads, refreshOfflineManifest]);

  useEffect(() => {
    if (!isSecureOfflineLibraryAvailable()) clearLegacyLibraryDownloads().catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("artico_library_theme", themeName);
  }, [themeName]);

  useEffect(() => {
    localStorage.setItem("artico_library_view", viewMode);
  }, [viewMode]);

  useEffect(() => {
    return () => {
      if (streamUrl && streamUrlOrigin === "remote") URL.revokeObjectURL(streamUrl);
      if (pdfUrl && pdfUrlOrigin === "remote") URL.revokeObjectURL(pdfUrl);
    };
  }, [streamUrl, streamUrlOrigin, pdfUrl, pdfUrlOrigin]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) =>
      `${item.title} ${item.author_name} ${item.description} ${item.category || ""} ${item.subcategory || ""}`.toLowerCase().includes(term)
    );
  }, [items, search]);

  const openItem = async (item) => {
    setSelected(item);
    setLoadingStream(true);
    if (streamUrl && streamUrlOrigin === "remote") URL.revokeObjectURL(streamUrl);
    setStreamUrl("");
    setStreamUrlOrigin("remote");
    setPdfUrl("");
    setPdfUrlOrigin("remote");
    setLocalPdfData(null);
    try {
      const hasLocalCopy = Boolean(downloadedItems[item.id]);
      if (isSecureOfflineLibraryAvailable() && hasLocalCopy) {
        if (item.media_type === "pdf") {
          const localItem = await readSecureLibraryItem(item.id);
          setLocalPdfData(localItem.data);
        } else {
          const localMediaUrl = await prepareSecureLibraryMedia(item.id, item.media_type);
          if (!localMediaUrl) throw new Error("local media unavailable");
          setStreamUrl(localMediaUrl);
          setStreamUrlOrigin("secure-local");
        }
        saveProgress(item, {
          percent: Math.max(Number(item.progress?.percent || 0), 5),
          current_page: item.progress?.current_page || 1,
        });
        return;
      }
      const remoteUrl = libraryAPI.getStreamUrl(item.id);
      if (item.media_type === "pdf") {
        saveProgress(item, { percent: Math.max(Number(item.progress?.percent || 0), 5), current_page: item.progress?.current_page || 1 });
        return;
      }
      const token = localStorage.getItem("mmj_token");
      const response = await fetch(remoteUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("stream failed");
      const blob = await response.blob();
      setStreamUrl(URL.createObjectURL(blob));
      setStreamUrlOrigin("remote");
    } catch (error) {
      toast.error("Impossible d'ouvrir cette œuvre");
    } finally {
      setLoadingStream(false);
    }
  };

  const closePlayer = () => {
    if (streamUrl && streamUrlOrigin === "remote") URL.revokeObjectURL(streamUrl);
    if (selected?.id && streamUrlOrigin === "secure-local") {
      clearSecureLibraryTemporaryMedia(selected.id).catch(() => {});
    }
    setStreamUrl("");
    setLocalPdfData(null);
    setSelected(null);
  };

  const saveProgress = useCallback(async (item, patch = {}) => {
    if (!item) return;
    try {
      await libraryAPI.saveProgress({
        product_id: item.id,
        media_type: item.media_type,
        current_page: patch.current_page || item.progress?.current_page || null,
        percent: patch.percent ?? item.progress?.percent ?? 0,
        position_seconds: patch.position_seconds ?? item.progress?.position_seconds ?? 0,
        duration_seconds: patch.duration_seconds ?? item.progress?.duration_seconds ?? 0,
      });
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (!selected || selected.media_type === "pdf") return undefined;
    const interval = setInterval(() => {
      const media = mediaRef.current;
      if (!media || !media.duration) return;
      saveProgress(selected, {
        position_seconds: media.currentTime,
        duration_seconds: media.duration,
        percent: Math.min(100, (media.currentTime / media.duration) * 100),
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [selected, saveProgress]);

  const toggleFavorite = async (item) => {
    const next = !item.is_favorite;
    setItems((prev) => prev.map((p) => p.id === item.id ? { ...p, is_favorite: next } : p));
    try {
      await libraryAPI.setFavorite(item.id, next);
    } catch (error) {
      setItems((prev) => prev.map((p) => p.id === item.id ? { ...p, is_favorite: !next } : p));
      toast.error("Favori non enregistré");
    }
  };

  const onPdfLoad = () => {
    if (selected?.media_type === "pdf") {
      saveProgress(selected, { percent: Math.max(Number(selected.progress?.percent || 0), 5), current_page: selected.progress?.current_page || 1 });
    }
  };

  const onLocalPdfPageChange = useCallback((page, percent) => {
    if (!selected || selected.media_type !== "pdf") return;
    saveProgress(selected, { current_page: page, percent });
  }, [saveProgress, selected]);

  const downloadSelectedForOffline = async () => {
    if (!selected) return;
    setDownloadingId(selected.id);
    try {
      const token = localStorage.getItem("mmj_token");
      const device = await getSecureLibraryIdentity();
      if (!device?.deviceId || !device?.publicKey) throw new Error("Appareil mobile sécurisé indisponible");
      await libraryAPI.registerOfflineDevice({ device_id: device.deviceId, public_key: device.publicKey });
      await downloadSecureLibraryItem({
        item: selected,
        url: libraryAPI.getOfflinePackageEndpoint(selected.id),
        authorization: token ? `Bearer ${token}` : "",
      });
      await refreshDownloads();
      toast.success("Téléchargement hors ligne terminé");
    } catch (error) {
      toast.error("Téléchargement hors ligne impossible");
    } finally {
      setDownloadingId("");
    }
  };

  const deleteSelectedDownload = async () => {
    if (!selected) return;
    const done = await removeSecureLibraryItem(selected.id);
    if (done) {
      await refreshDownloads();
      toast.success("Téléchargement hors ligne supprimé");
    } else {
      toast.error("Suppression impossible");
    }
  };

  const selectedCoverPath = selected?.cover_image_path || selected?.thumbnail_path;
  const selectedCoverUrl = selectedCoverPath ? getFileUrl(selectedCoverPath) : undefined;
  const selectedDownload = selected ? downloadedItems[selected.id] : null;
  const manifestItemsCount = offlineManifest?.items?.length || 0;

  return (
    <div className={`min-h-[calc(100vh-5rem)] -mx-6 md:-mx-8 lg:-mx-12 px-6 md:px-8 lg:px-12 pb-12 ${theme.shell}`} onContextMenu={(e) => e.preventDefault()}>
      <div className={`rounded-none md:rounded-b-[32px] bg-gradient-to-br ${theme.hero} px-6 py-8 md:px-10 md:py-12 text-white shadow-xl`}>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3 text-white/90">
              <img src={articoLogo} alt="ARTICO SARLU" className="h-28 w-28 rounded-3xl bg-white/95 object-contain p-2 shadow-lg" />
              <span className="text-sm font-semibold uppercase tracking-[0.2em]">Artico Library</span>
            </div>
            <h1 className="mt-4 max-w-3xl text-2xl font-semibold font-['Outfit'] md:text-4xl">Votre salon privé de lecture, d'écoute et de visionnage.</h1>
            <p className="mt-4 max-w-2xl text-white/80">Streaming sécurisé des œuvres achetées et de vos créations, avec reprise automatique.</p>
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {Object.entries(themes).map(([key, item]) => (
                <Button
                  key={key}
                  variant={themeName === key ? "secondary" : "outline"}
                  className={themeName === key ? "rounded-full bg-white text-[#2A4B46]" : "rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20"}
                  onClick={() => setThemeName(key)}
                >
                  {key === "night" && <Moon size={16} className="mr-2" />}
                  {item.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {Object.entries(libraryViews).map(([key, item]) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={key}
                    variant={viewMode === key ? "secondary" : "outline"}
                    className={viewMode === key ? "rounded-full bg-white text-[#2A4B46]" : "rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20"}
                    onClick={() => setViewMode(key)}
                  >
                    <Icon size={16} className="mr-2" />
                    {item.label}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20"
                onClick={refreshOfflineManifest}
                disabled={syncingOfflineManifest}
              >
                {syncingOfflineManifest ? "Synchronisation..." : "Synchroniser les droits"}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-8 flex max-w-xl items-center gap-3 rounded-full bg-white/15 px-4 py-3 backdrop-blur">
          <Search size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une œuvre, un auteur..."
            className="w-full bg-transparent text-sm text-white placeholder:text-white/65 outline-none"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge className="rounded-full bg-white/15 text-white">
            {manifestItemsCount} œuvre{manifestItemsCount > 1 ? "s" : ""} autorisée{manifestItemsCount > 1 ? "s" : ""}
          </Badge>
          {offlineManifest?.synced_at ? (
            <Badge className="rounded-full bg-white/15 text-white/90">
              Synchro hors ligne: {new Date(offlineManifest.synced_at).toLocaleString()}
            </Badge>
          ) : null}
        </div>
      </div>

      <LibraryView
        mode={viewMode}
        items={filteredItems}
        theme={theme}
        onOpen={openItem}
        onFavorite={toggleFavorite}
      />

      <div className={viewMode === "legacy" ? "mt-8 space-y-10" : "hidden"}>
        {sections.map((section) => {
          const rows = filteredItems.filter(section.filter);
          if (rows.length === 0) return null;
          const Icon = section.icon;
          return (
            <section key={section.key}>
              <div className="mb-4 flex items-center gap-3">
                <Icon className={theme.text} size={24} />
                <h2 className={`text-2xl font-bold font-['Outfit'] ${theme.text}`}>{section.title}</h2>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                {rows.map((item) => (
                  <MediaCard key={`${section.key}-${item.id}`} item={item} theme={theme} onOpen={openItem} onFavorite={toggleFavorite} />
                ))}
              </div>
            </section>
          );
        })}

        {filteredItems.length === 0 && (
          <div className={`rounded-3xl border ${theme.panel} p-10 text-center`}>
            <Library className={`mx-auto ${theme.text}`} size={42} />
            <p className={`mt-4 text-lg font-semibold ${theme.text}`}>Votre bibliothèque est vide.</p>
            <p className={`mt-2 text-sm ${theme.muted}`}>Achetez une œuvre validée ou publiez votre propre contenu pour la voir apparaître ici.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link to="/catalogue">
                <Button className="rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
                  Ouvrir le catalogue
                </Button>
              </Link>
              <Link to="/public/catalogue">
                <Button variant="outline" className="rounded-full border-[#2A4B46] text-[#2A4B46] hover:bg-[#2A4B46] hover:text-white">
                  Parcourir le catalogue public
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && closePlayer()}>
        <DialogContent className="h-[100dvh] w-screen max-w-none overflow-hidden rounded-none border-0 bg-[#070A0B] p-0 text-white sm:h-[94dvh] sm:max-w-6xl sm:rounded-2xl" onContextMenu={(e) => e.preventDefault()}>
          <DialogHeader className="px-4 pt-4 sm:px-5 sm:pt-5">
            <DialogTitle className="flex items-center gap-3 text-white">
              <MediaIcon type={selected?.media_type} className="text-[#D4A373]" />
              {selected?.title || "Artico Player"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex h-[calc(100dvh-4.5rem)] flex-col px-0 pb-0 sm:h-[calc(94dvh-4.5rem)] sm:px-5 sm:pb-5">
            <div className="min-h-0 flex-1 overflow-hidden bg-black sm:rounded-2xl">
              {loadingStream && <div className="flex h-full min-h-[320px] items-center justify-center text-white/70">Préparation du flux sécurisé...</div>}
              {!loadingStream && selected?.media_type === "pdf" && !localPdfData && (
                <ProtectedPdfReader
                  productId={selected.id}
                  item={selected}
                  onPageChange={onLocalPdfPageChange}
                />
              )}
              {!loadingStream && localPdfData && selected?.media_type === "pdf" && (
                <PdfCanvasReader
                  data={localPdfData}
                  item={selected}
                  onPageChange={onLocalPdfPageChange}
                />
              )}
              {!loadingStream && streamUrl && selected?.media_type === "audio" && (
                <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-6 bg-gradient-to-br from-[#111819] to-[#2A4B46] p-6 sm:p-8">
                  <Headphones size={72} className="text-[#D4A373]" />
                  <audio ref={mediaRef} src={streamUrl} controls controlsList="nodownload noplaybackrate" className="w-full max-w-2xl" />
                </div>
              )}
              {!loadingStream && streamUrl && selected?.media_type === "video" && (
                <video ref={mediaRef} src={streamUrl} poster={selectedCoverUrl} controls controlsList="nodownload noplaybackrate" className="h-full min-h-[320px] w-full bg-black object-contain" />
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-4 py-3 sm:border-t-0 sm:px-0 sm:pt-4">
              <div className="space-y-1 text-sm text-white/65">
                <p>{isSecureOfflineLibraryAvailable() ? "Lecture hors ligne chiffrée, réservée à l'application MMJ." : "Lecture en ligne protégée : les documents ne sont pas téléchargeables depuis le site."}</p>
                {selectedDownload ? (
                  <p className="text-xs text-[#D4A373]">Disponible hors ligne depuis {new Date(selectedDownload.downloaded_at).toLocaleString()}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {selected?.is_author && (
                  <a href={libraryAPI.getSourceUrl(selected.id)} target="_blank" rel="noreferrer">
                    <Button className="gap-2 bg-[#D4A373] text-white hover:bg-[#c08d5c]">
                      <Download size={17} /> Télécharger ma source
                    </Button>
                  </a>
                )}
                {selected && isSecureOfflineLibraryAvailable() && !selectedDownload && (
                  <Button
                    onClick={downloadSelectedForOffline}
                    disabled={loadingStream || Boolean(downloadingId)}
                    className="gap-2 bg-white text-[#0A0F1A] hover:bg-white/90"
                  >
                    <Download size={17} />
                    {downloadingId === selected.id ? "Téléchargement..." : "Lecture hors ligne"}
                  </Button>
                )}
                {selected && isSecureOfflineLibraryAvailable() && selectedDownload && (
                  <Button
                    onClick={deleteSelectedDownload}
                    variant="outline"
                    className="gap-2 border-white/15 bg-white/5 text-white hover:bg-white/10"
                  >
                    Supprimer hors ligne
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LibraryView({ mode, items, theme, onOpen, onFavorite }) {
  if (!items.length) {
    return (
      <div className={`mt-8 rounded-3xl border ${theme.panel} p-10 text-center`}>
        <Library className={`mx-auto ${theme.text}`} size={42} />
        <p className={`mt-4 text-lg font-semibold ${theme.text}`}>Votre bibliotheque est vide.</p>
        <p className={`mt-2 text-sm ${theme.muted}`}>Achetez une oeuvre validee ou publiez votre propre contenu pour la voir apparaitre ici.</p>
      </div>
    );
  }
  if (mode === "collection") {
    return <CollectionLibrary items={items} theme={theme} onOpen={onOpen} onFavorite={onFavorite} />;
  }
  if (mode === "streaming") {
    return <StreamingLibrary items={items} theme={theme} onOpen={onOpen} onFavorite={onFavorite} />;
  }
  return <PremiumStreamingLibrary items={items} theme={theme} onOpen={onOpen} onFavorite={onFavorite} />;
}

function PremiumStreamingLibrary({ items, theme, onOpen, onFavorite }) {
  const featured = items.find((item) => Number(item.progress?.percent || 0) > 1) || items[0];
  const continueItems = items.filter((item) => Number(item.progress?.percent || 0) > 1 && item.id !== featured?.id).slice(0, 3);

  return (
    <div className="mt-8 space-y-10">
      {featured && (
        <section className={`rounded-[32px] border ${theme.panel} overflow-hidden shadow-xl`}>
          <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="relative min-h-[360px] overflow-hidden bg-[#111819] p-6 text-white md:p-8">
              <LibraryCover item={featured} className="absolute inset-0 h-full w-full" imageClassName="scale-105 opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/15" />
              <div className="relative z-10 flex min-h-[310px] max-w-xl flex-col justify-end">
                <Badge className="mb-4 w-fit rounded-full bg-white/15 text-white">A continuer</Badge>
                <p className="text-sm uppercase tracking-[0.22em] text-white/65">{mediaTypeLabel(featured)}</p>
                <h2 className="mt-3 font-['Outfit'] text-4xl font-black leading-tight md:text-6xl">{featured.title}</h2>
                <p className="mt-4 text-white/70">{featured.author_name || "ARTICO"}</p>
                <Button onClick={() => onOpen(featured)} className="mt-6 w-fit rounded-full bg-white text-[#0A2540] hover:bg-white/90">
                  {actionLabel(featured)} <Play size={16} className="ml-2" />
                </Button>
              </div>
            </div>
            <div className="p-5 md:p-6">
              <h3 className={`font-['Outfit'] text-2xl font-black ${theme.text}`}>Reprendre rapidement</h3>
              <div className="mt-4 space-y-3">
                {(continueItems.length ? continueItems : items.slice(1, 4)).map((item) => (
                  <LibraryListRow key={item.id} item={item} theme={theme} onOpen={onOpen} onFavorite={onFavorite} compact />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      <SectionRows items={items} theme={theme} onOpen={onOpen} onFavorite={onFavorite} card="poster" />
    </div>
  );
}

function StreamingLibrary({ items, theme, onOpen, onFavorite }) {
  return (
    <div className="mt-8 rounded-[32px] bg-[#070A0F] px-4 py-8 text-white shadow-xl md:px-7">
      <div className="mb-8 flex flex-col gap-2">
        <Badge className="w-fit rounded-full bg-white/10 text-white">Mode Netflix</Badge>
        <h2 className="font-['Outfit'] text-4xl font-black">Votre streaming MMJ</h2>
        <p className="max-w-2xl text-white/60">Des rangees horizontales par type de contenu, pensees pour regarder, ecouter et reprendre vite.</p>
      </div>
      <SectionRows items={items} theme={{ ...theme, text: "text-white", muted: "text-white/60", panel: "bg-white/10 border-white/10" }} onOpen={onOpen} onFavorite={onFavorite} card="poster" dark />
    </div>
  );
}

function CollectionLibrary({ items, theme, onOpen, onFavorite }) {
  const categories = [...new Set(items.map((item) => getProductCategoryLabel(item.category)).filter(Boolean))].slice(0, 6);
  return (
    <div className="mt-8 grid gap-6 xl:grid-cols-[280px_1fr]">
      <aside className={`h-fit rounded-3xl border ${theme.panel} p-4`}>
        <p className={`font-['Outfit'] text-xl font-black ${theme.text}`}>Collection</p>
        <div className="mt-4 space-y-2">
          {["Tous", "Favoris", "A continuer", ...categories].map((tag) => (
            <div key={tag} className={`flex items-center justify-between rounded-2xl px-3 py-2 text-sm ${theme.muted}`}>
              <span>{tag}</span>
              <span>{tag === "Tous" ? items.length : Math.max(1, Math.floor(items.length / 3))}</span>
            </div>
          ))}
        </div>
      </aside>
      <section className="space-y-3">
        {items.map((item) => (
          <LibraryListRow key={item.id} item={item} theme={theme} onOpen={onOpen} onFavorite={onFavorite} />
        ))}
      </section>
    </div>
  );
}

function SectionRows({ items, theme, onOpen, onFavorite, card = "poster", dark = false }) {
  return (
    <div className="space-y-9">
      {sections.map((section) => {
        const rows = items.filter(section.filter);
        if (!rows.length) return null;
        const Icon = section.icon;
        return (
          <section key={section.key}>
            <div className="mb-4 flex items-center gap-3">
              <Icon className={theme.text} size={24} />
              <h2 className={`text-2xl font-bold font-['Outfit'] ${theme.text}`}>{section.title}</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3">
              {rows.map((item) => (
                card === "poster" ? (
                  <LibraryPosterCard key={`${section.key}-${item.id}`} item={item} theme={theme} onOpen={onOpen} onFavorite={onFavorite} dark={dark} />
                ) : (
                  <MediaCard key={`${section.key}-${item.id}`} item={item} theme={theme} onOpen={onOpen} onFavorite={onFavorite} />
                )
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function LibraryPosterCard({ item, theme, onOpen, onFavorite, dark = false }) {
  const percent = Number(item.progress?.percent || 0);
  return (
    <div className={`w-48 shrink-0 overflow-hidden rounded-2xl border ${theme.panel} shadow-sm transition hover:-translate-y-1 hover:shadow-xl`}>
      <div className="relative aspect-[4/5] overflow-hidden bg-[#111819] text-white">
        <LibraryCover item={item} className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5" />
        <button onClick={() => onFavorite(item)} className="absolute right-3 top-3 z-20 rounded-full bg-black/25 p-2 backdrop-blur" aria-label="Favori">
          <Heart size={17} className={item.is_favorite ? "fill-[#D4A373] text-[#D4A373]" : "text-white"} />
        </button>
        <div className="absolute bottom-4 left-4 right-4 z-10">
          <p className="text-xs uppercase tracking-[0.18em] text-white/65">{mediaTypeLabel(item)}</p>
          <p className="mt-2 line-clamp-2 font-['Outfit'] text-xl font-black">{item.title}</p>
        </div>
      </div>
      <div className="p-3">
        <p className={`line-clamp-1 text-sm font-medium ${theme.text}`}>{item.author_name || "ARTICO"}</p>
        <Progress value={percent} className="mt-3 h-1.5 bg-[#E6E4E0] [&>div]:bg-[#D4A373]" />
        <Button onClick={() => onOpen(item)} className={`mt-3 w-full rounded-full ${dark ? "bg-white text-[#0A0F1A] hover:bg-white/90" : "bg-[#2A4B46] text-white hover:bg-[#1f3a36]"}`}>
          {actionLabel(item)}
        </Button>
      </div>
    </div>
  );
}

function LibraryListRow({ item, theme, onOpen, onFavorite, compact = false }) {
  const percent = Number(item.progress?.percent || 0);
  return (
    <div className={`flex gap-4 rounded-2xl border ${theme.panel} p-3 shadow-sm`}>
      <div className={compact ? "h-24 w-16 shrink-0 overflow-hidden rounded-xl" : "h-28 w-20 shrink-0 overflow-hidden rounded-xl"}>
        <LibraryCover item={item} className="h-full w-full" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`line-clamp-1 font-semibold ${theme.text}`}>{item.title}</p>
            <p className={`mt-1 text-xs ${theme.muted}`}>{mediaTypeLabel(item)} - {item.author_name || "ARTICO"}</p>
          </div>
          <button onClick={() => onFavorite(item)} className="shrink-0 rounded-full p-1" aria-label="Favori">
            <Heart size={18} className={item.is_favorite ? "fill-[#D4A373] text-[#D4A373]" : theme.muted} />
          </button>
        </div>
        <p className={`mt-2 line-clamp-2 text-xs ${theme.muted}`}>{item.description || getProductCategoryLabel(item.category)}</p>
        <div className="mt-3 flex items-center gap-3">
          <Progress value={percent} className="h-1.5 flex-1 bg-[#E6E4E0] [&>div]:bg-[#D4A373]" />
          <Button size="sm" onClick={() => onOpen(item)} className="rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
            {actionLabel(item)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LibraryCover({ item, className = "", imageClassName = "" }) {
  const thumbnailPath = item.thumbnail_path || item.cover_image_path;
  const coverUrl = thumbnailPath ? getFileUrl(thumbnailPath) : null;
  if (coverUrl) {
    return <img src={coverUrl} alt={`Couverture de ${item.title}`} className={`object-cover ${className} ${imageClassName}`} loading="lazy" decoding="async" />;
  }
  return (
    <div className={`flex flex-col justify-between bg-gradient-to-br from-[#2A4B46] via-[#386A62] to-[#D4A373] p-4 text-white ${className}`}>
      <MediaIcon type={item.media_type} className="h-9 w-9 opacity-90" />
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-white/65">{mediaTypeLabel(item)}</p>
        <p className="mt-2 line-clamp-3 font-['Outfit'] text-xl font-black">{item.title}</p>
      </div>
    </div>
  );
}

function mediaTypeLabel(item) {
  if (item.media_type === "video") return "Video";
  if (item.media_type === "audio") return "Audio";
  return "Document texte";
}

function MediaCard({ item, theme, onOpen, onFavorite }) {
  const percent = Number(item.progress?.percent || 0);
  const thumbnailPath = item.thumbnail_path || item.cover_image_path;
  const coverUrl = thumbnailPath ? getFileUrl(thumbnailPath) : null;
  const authorPhotoUrl = item.author_profile_photo_path ? getPublicAuthorPhotoUrl(item.author_profile_photo_path) : null;
  const typeLabel = item.media_type === "video" ? "Video" : item.media_type === "audio" ? "Audio" : "Livre";
  return (
    <div className={`group overflow-hidden rounded-2xl border ${theme.panel} shadow-sm transition hover:-translate-y-1 hover:shadow-xl`}>
      <div className={`relative aspect-[4/5] overflow-hidden text-white ${coverUrl ? "bg-[#111819]" : "bg-gradient-to-br from-[#2A4B46] via-[#386A62] to-[#D4A373] p-5"}`}>
        {coverUrl && (
          <>
            <img
              src={coverUrl}
              alt={`Couverture de ${item.title}`}
              className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
          </>
        )}
        <button
          onClick={() => onFavorite(item)}
          className="absolute right-3 top-3 z-20 rounded-full bg-black/25 p-2 backdrop-blur transition hover:bg-black/40"
          aria-label="Favori"
        >
          <Heart size={18} className={item.is_favorite ? "fill-[#D4A373] text-[#D4A373]" : "text-white"} />
        </button>
        {item.is_author && <Badge className="absolute left-3 top-3 z-20 rounded-full bg-[#D4A373] text-white">Auteur</Badge>}
        <AuthorAvatar url={authorPhotoUrl} name={item.author_name} />
        <div className={`flex h-full flex-col justify-end ${coverUrl ? "absolute inset-0 z-10 p-5" : ""}`}>
          <MediaIcon type={item.media_type} className="mb-5 h-12 w-12 opacity-90" />
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">{typeLabel}</p>
          <h3 className="mt-2 line-clamp-3 text-2xl font-black font-['Outfit']">{item.title}</h3>
        </div>
      </div>
      <div className="p-4">
        <p className={`text-sm font-medium ${theme.text}`}>{item.author_name || "ARTICO"}</p>
        <p className={`mt-1 text-xs ${theme.muted}`}>
          {getProductCategoryLabel(item.category)}{item.subcategory ? ` / ${item.subcategory}` : ""}
        </p>
        <p className={`mt-1 line-clamp-2 text-xs ${theme.muted}`}>{item.description}</p>
        <div className="mt-4">
          <div className="mb-1 flex justify-between text-xs">
            <span className={theme.muted}>Progression</span>
            <span className={theme.text}>{Math.round(percent)}%</span>
          </div>
          <Progress value={percent} className="h-1.5 bg-[#E6E4E0] [&>div]:bg-[#D4A373]" />
        </div>
        <Button onClick={() => onOpen(item)} className="mt-4 w-full gap-2 rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
          {percent > 0 && percent < 95 ? <Pause size={16} /> : <Play size={16} />}
          {actionLabel(item)}
        </Button>
      </div>
    </div>
  );
}

function AuthorAvatar({ url, name }) {
  return (
    <div className="absolute bottom-3 right-3 z-20 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-[#2A4B46] text-sm font-bold text-white shadow-lg">
      {url ? <img src={url} alt={`Photo de ${name || "auteur"}`} className="h-full w-full object-cover" loading="lazy" /> : getAuthorInitials(name)}
    </div>
  );
}

function getAuthorInitials(name = "") {
  const parts = String(name || "A").trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || "A"}${parts[1]?.[0] || ""}`.toUpperCase();
}

function ProtectedPdfReader({ productId, item, onPageChange }) {
  const shellRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(Number(item.progress?.current_page || 1));
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    libraryAPI.getProtectedPdfMeta(productId)
      .then((response) => {
        if (!active) return;
        const count = Math.max(Number(response.data?.page_count || 1), 1);
        setPageCount(count);
        setPageNumber((current) => Math.min(Math.max(current, 1), count));
      })
      .catch(() => toast.error("Le lecteur sécurisé n'a pas pu préparer ce document."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [productId]);

  useEffect(() => {
    if (pageCount) onPageChange(pageNumber, Math.round((pageNumber / pageCount) * 100));
  }, [onPageChange, pageCount, pageNumber]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (["INPUT", "TEXTAREA"].includes(event.target?.tagName)) return;
      if (event.key === "ArrowRight" || event.key === "PageDown") {
        event.preventDefault();
        setPageNumber((current) => Math.min(current + 1, pageCount || current));
      } else if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault();
        setPageNumber((current) => Math.max(current - 1, 1));
      } else if (event.key === "+" || event.key === "=") {
        setZoom((current) => Math.min(2.5, current + 0.15));
      } else if (event.key === "-") {
        setZoom((current) => Math.max(0.6, current - 0.15));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pageCount]);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await shellRef.current?.requestFullscreen?.();
    } catch (error) {
      toast.error("Le mode plein écran n'est pas disponible.");
    }
  };

  const imageUrl = pageCount ? libraryAPI.getProtectedPdfPageUrl(productId, pageNumber) : "";
  return (
    <div ref={shellRef} className="flex h-full min-h-[320px] flex-col bg-[#101516]" onContextMenu={(event) => event.preventDefault()}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-black/30 px-3 py-2 text-sm text-white">
        <div className="flex items-center gap-2">
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" disabled={pageNumber <= 1} onClick={() => setPageNumber((current) => Math.max(current - 1, 1))} aria-label="Page précédente"><ChevronLeft size={18} /></Button>
          <span className="min-w-24 text-center text-xs text-white/75">Page {pageNumber} / {pageCount || "..."}</span>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" disabled={!pageCount || pageNumber >= pageCount} onClick={() => setPageNumber((current) => Math.min(current + 1, pageCount))} aria-label="Page suivante"><ChevronRight size={18} /></Button>
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" onClick={() => setZoom((current) => Math.max(0.6, current - 0.15))} aria-label="Réduire"><ZoomOut size={17} /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" onClick={() => setZoom((current) => Math.min(2.5, current + 0.15))} aria-label="Agrandir"><ZoomIn size={17} /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/10 hover:text-white" onClick={toggleFullscreen} aria-label="Plein écran"><Maximize2 size={17} /></Button>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 items-start justify-center overflow-auto p-3 sm:p-5">
        {loading && <div className="mt-10 rounded-full bg-black/60 px-4 py-2 text-sm text-white/80">Préparation du lecteur sécurisé...</div>}
        {!loading && imageUrl && (
          <img key={imageUrl} src={imageUrl} alt={`Page ${pageNumber} de ${item.title}`} draggable="false" onError={() => toast.error("Impossible de charger cette page protégée.")} className="max-w-none select-none rounded-sm bg-white shadow-2xl" style={{ width: `${Math.round(760 * zoom)}px` }} />
        )}
      </div>
    </div>
  );
}

function PdfNativeReader({ url, item, onLoad }) {
  const shellRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(async () => {
    const node = shellRef.current;
    if (!node) return;
    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (error) {
      toast.error("Le mode plein ecran n'est pas disponible");
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  return (
    <div ref={shellRef} className="flex h-full min-h-[360px] flex-col bg-[#080A0B] fullscreen:h-screen">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-[#080A0B] px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{item.title}</p>
          <p className="text-xs text-white/55">Lecteur PDF integre du navigateur</p>
        </div>
        <IconButton label={isFullscreen ? "Quitter plein ecran" : "Plein ecran"} active={isFullscreen} onClick={toggleFullscreen}>
          <Maximize2 size={17} />
        </IconButton>
      </div>
      <iframe
        title={`Lecture PDF - ${item.title}`}
        src={url}
        onLoad={onLoad}
        className="h-full min-h-0 flex-1 border-0 bg-[#2A2A2A]"
      />
    </div>
  );
}

function PdfContinuousReader({ data, item, onPageChange }) {
  const shellRef = useRef(null);
  const stageRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [activePage, setActivePage] = useState(Number(item.progress?.current_page || 1));
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import("pdfjs-dist/legacy/build/pdf.mjs")
      .then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.mjs",
          import.meta.url
        ).toString();
        return pdfjs.getDocument({ data: data.slice(0) }).promise;
      })
      .then((doc) => {
        if (cancelled) return;
        setPdfDoc(doc);
        setPageCount(doc.numPages);
        const startPage = Math.min(Math.max(Number(item.progress?.current_page || 1), 1), doc.numPages);
        setActivePage(startPage);
        window.requestAnimationFrame(() => {
          stageRef.current?.querySelector(`[data-page="${startPage}"]`)?.scrollIntoView({ block: "start" });
        });
      })
      .catch((error) => {
        console.error("PDF.js error", error);
        toast.error("Le lecteur PDF n'a pas pu ouvrir ce fichier");
      });
    return () => {
      cancelled = true;
    };
  }, [data, item.progress?.current_page]);

  const toggleFullscreen = useCallback(async () => {
    const node = shellRef.current;
    if (!node) return;
    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (error) {
      toast.error("Le mode plein ecran n'est pas disponible");
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const scrollToPage = useCallback((page) => {
    const next = Math.min(Math.max(page, 1), pageCount || 1);
    stageRef.current?.querySelector(`[data-page="${next}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActivePage(next);
  }, [pageCount]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const tagName = event.target?.tagName?.toLowerCase();
      if (tagName === "input" || tagName === "textarea") return;
      if (event.key === "ArrowRight" || event.key === "PageDown") {
        event.preventDefault();
        scrollToPage(activePage + 1);
      }
      if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault();
        scrollToPage(activePage - 1);
      }
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        setZoom((value) => Math.min(2.4, Number((value + 0.15).toFixed(2))));
      }
      if (event.key === "-") {
        event.preventDefault();
        setZoom((value) => Math.max(0.55, Number((value - 0.15).toFixed(2))));
      }
      if (event.key.toLowerCase() === "f") {
        event.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activePage, scrollToPage, toggleFullscreen]);

  const handleVisiblePage = useCallback((page) => {
    setActivePage(page);
    const percent = pageCount ? Math.min(100, (page / pageCount) * 100) : 0;
    onPageChange(page, percent);
  }, [onPageChange, pageCount]);

  const pages = useMemo(() => Array.from({ length: pageCount }, (_, index) => index + 1), [pageCount]);

  return (
    <div ref={shellRef} className="flex h-full min-h-[360px] flex-col bg-[#080A0B] fullscreen:h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-[#080A0B] px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{item.title}</p>
          <p className="text-xs text-white/55">Page {activePage} sur {pageCount || "..."}</p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton label="Page precedente" disabled={activePage <= 1} onClick={() => scrollToPage(activePage - 1)}>
            <ChevronLeft size={17} />
          </IconButton>
          <span className="min-w-12 text-center text-xs text-white/70">{Math.round(zoom * 100)}%</span>
          <IconButton label="Zoom arriere" onClick={() => setZoom((value) => Math.max(0.55, Number((value - 0.15).toFixed(2))))}>
            <ZoomOut size={17} />
          </IconButton>
          <IconButton label="Zoom avant" onClick={() => setZoom((value) => Math.min(2.4, Number((value + 0.15).toFixed(2))))}>
            <ZoomIn size={17} />
          </IconButton>
          <IconButton label="Page suivante" disabled={!pageCount || activePage >= pageCount} onClick={() => scrollToPage(activePage + 1)}>
            <ChevronRight size={17} />
          </IconButton>
          <IconButton label={isFullscreen ? "Quitter plein ecran" : "Plein ecran"} active={isFullscreen} onClick={toggleFullscreen}>
            <Maximize2 size={17} />
          </IconButton>
        </div>
      </div>
      <div ref={stageRef} className="min-h-0 flex-1 overflow-auto bg-[radial-gradient(circle_at_top,#2A2019,#100D0A_55%,#080605)] px-3 py-6 sm:px-6">
        {!pdfDoc && <div className="flex h-full items-center justify-center text-sm text-white/65">Preparation du lecteur PDF...</div>}
        {pdfDoc && (
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-6 pb-10">
            {pages.map((page) => (
              <PdfContinuousPage key={page} pdfDoc={pdfDoc} pageNumber={page} zoom={zoom} onVisible={handleVisiblePage} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PdfContinuousPage({ pdfDoc, pageNumber, zoom, onVisible }) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [width, setWidth] = useState(720);
  const [visible, setVisible] = useState(pageNumber <= 2);

  useEffect(() => {
    const updateWidth = () => {
      const wrapper = wrapperRef.current;
      const wrapperRect = wrapper?.getBoundingClientRect();
      const stageRect = wrapper?.closest(".overflow-auto")?.getBoundingClientRect();
      setWidth(Math.max(
        280,
        wrapper?.clientWidth || 0,
        wrapperRect?.width || 0,
        stageRect ? stageRect.width - 48 : 0,
        720
      ));
    };
    updateWidth();
    const delayedChecks = [setTimeout(updateWidth, 120), setTimeout(updateWidth, 420), setTimeout(updateWidth, 900)];
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateWidth) : null;
    if (wrapperRef.current && observer) observer.observe(wrapperRef.current);
    window.addEventListener("resize", updateWidth);
    return () => {
      delayedChecks.forEach(clearTimeout);
      observer?.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
          onVisible(pageNumber);
        }
      });
    }, { root: node.closest(".overflow-auto"), rootMargin: "420px 0px", threshold: 0.15 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [onVisible, pageNumber]);

  useEffect(() => {
    if (!visible || !pdfDoc || !canvasRef.current) return undefined;
    let cancelled = false;
    renderTaskRef.current?.cancel?.();
    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return null;
      const baseViewport = page.getViewport({ scale: 1 });
      const fitScale = Math.min(2.2, Math.max(0.35, (width - 16) / baseViewport.width));
      const viewport = page.getViewport({ scale: fitScale * zoom });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d", { alpha: false });
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, viewport.width, viewport.height);
      renderTaskRef.current = page.render({ canvasContext: context, viewport });
      return renderTaskRef.current.promise;
    }).catch((error) => {
      if (error?.name !== "RenderingCancelledException") console.error("PDF page render error", error);
    });
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [pdfDoc, pageNumber, visible, width, zoom]);

  return (
    <div ref={wrapperRef} data-page={pageNumber} className="flex w-full scroll-mt-4 flex-col items-center gap-2">
      <canvas ref={canvasRef} className="h-auto max-w-full rounded-md bg-white shadow-2xl" />
      <span className="rounded-full bg-black/35 px-3 py-1 text-xs text-white/60">Page {pageNumber}</span>
    </div>
  );
}

function PdfCanvasReaderLegacy({ data, item, onPageChange }) {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNumber, setPageNumber] = useState(Number(item.progress?.current_page || 1));
  const [pageCount, setPageCount] = useState(0);
  const [rendering, setRendering] = useState(true);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setRendering(true);
    import("pdfjs-dist/legacy/build/pdf.mjs")
      .then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.mjs",
          import.meta.url
        ).toString();
        return pdfjs.getDocument({ data: data.slice(0) }).promise;
      })
      .then((doc) => {
        if (cancelled) return;
        setPdfDoc(doc);
        setPageCount(doc.numPages);
        setPageNumber(Math.min(Math.max(Number(item.progress?.current_page || 1), 1), doc.numPages));
      })
      .catch((error) => {
        console.error("PDF.js error", error);
        toast.error("Le lecteur PDF n'a pas pu ouvrir ce fichier");
      })
      .finally(() => {
        if (!cancelled) setRendering(false);
      });
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [data, item.progress?.current_page]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return undefined;
    let cancelled = false;
    setRendering(true);
    renderTaskRef.current?.cancel?.();
    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const containerWidth = Math.min(980, Math.max(320, canvasRef.current.parentElement?.clientWidth || 800));
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      renderTaskRef.current = page.render({ canvasContext: context, viewport });
      return renderTaskRef.current.promise;
    }).then(() => {
      if (cancelled) return;
      setRendering(false);
      const percent = pageCount ? Math.min(100, (pageNumber / pageCount) * 100) : 0;
      onPageChange(pageNumber, percent);
    }).catch((error) => {
      if (error?.name !== "RenderingCancelledException") {
        console.error("PDF render error", error);
        toast.error("Erreur de rendu PDF");
      }
      setRendering(false);
    });
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [pdfDoc, pageNumber, pageCount, onPageChange]);

  const goToPage = (nextPage) => {
    setPageNumber(Math.min(Math.max(nextPage, 1), pageCount || 1));
  };

  return (
    <div className="flex min-h-[70vh] flex-col bg-[#15100B]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-white">{item.title}</p>
          <p className="text-xs text-white/60">
            Page {pageNumber} / {pageCount || "..."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10" disabled={pageNumber <= 1 || rendering} onClick={() => goToPage(pageNumber - 1)}>
            Précédent
          </Button>
          <Button className="bg-[#D4A373] text-white hover:bg-[#c08d5c]" disabled={!pageCount || pageNumber >= pageCount || rendering} onClick={() => goToPage(pageNumber + 1)}>
            Suivant
          </Button>
        </div>
      </div>
      <div className="flex flex-1 justify-center overflow-auto p-4">
        {rendering && <div className="absolute mt-10 rounded-full bg-black/60 px-4 py-2 text-sm text-white">Rendu PDF...</div>}
        <canvas ref={canvasRef} className="max-w-full rounded-lg bg-white shadow-2xl" />
      </div>
    </div>
  );
}

function PdfCanvasReader({ data, item, onPageChange }) {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNumber, setPageNumber] = useState(Number(item.progress?.current_page || 1));
  const [pageInput, setPageInput] = useState(String(item.progress?.current_page || 1));
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fitWidth, setFitWidth] = useState(true);
  const [rendering, setRendering] = useState(true);
  const [stageWidth, setStageWidth] = useState(900);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const getUsableStageWidth = useCallback(() => {
    const stage = stageRef.current;
    const dialog = stage?.closest("[role='dialog']");
    const stageMeasured = stage?.clientWidth || 0;
    const dialogMeasured = dialog?.clientWidth ? dialog.clientWidth - 40 : 0;
    const viewportMeasured = window.innerWidth ? Math.min(window.innerWidth - 96, 1120) : 900;
    const measured = Math.max(stageMeasured, dialogMeasured, viewportMeasured);
    return Math.max(280, measured - 24);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRendering(true);
    import("pdfjs-dist/legacy/build/pdf.mjs")
      .then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.mjs",
          import.meta.url
        ).toString();
        return pdfjs.getDocument({ data: data.slice(0) }).promise;
      })
      .then((doc) => {
        if (cancelled) return;
        const startPage = Math.min(Math.max(Number(item.progress?.current_page || 1), 1), doc.numPages);
        setPdfDoc(doc);
        setPageCount(doc.numPages);
        setPageNumber(startPage);
        setPageInput(String(startPage));
      })
      .catch((error) => {
        console.error("PDF.js error", error);
        toast.error("Le lecteur PDF n'a pas pu ouvrir ce fichier");
      })
      .finally(() => {
        if (!cancelled) setRendering(false);
      });
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [data, item.progress?.current_page]);

  useEffect(() => {
    let frameId = null;
    const updateStageWidth = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        setStageWidth(getUsableStageWidth());
      });
    };

    updateStageWidth();
    const delayedChecks = [setTimeout(updateStageWidth, 80), setTimeout(updateStageWidth, 260)];
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateStageWidth) : null;
    if (stageRef.current && observer) observer.observe(stageRef.current);
    window.addEventListener("resize", updateStageWidth);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      delayedChecks.forEach(clearTimeout);
      observer?.disconnect();
      window.removeEventListener("resize", updateStageWidth);
    };
  }, [getUsableStageWidth]);

  useEffect(() => {
    if (!pdfDoc) return undefined;
    let attempts = 0;
    const intervalId = setInterval(() => {
      setStageWidth(getUsableStageWidth());
      attempts += 1;
      if (attempts >= 12) clearInterval(intervalId);
    }, 120);
    return () => clearInterval(intervalId);
  }, [getUsableStageWidth, pdfDoc]);

  const goToPage = useCallback((nextPage) => {
    const bounded = Math.min(Math.max(nextPage, 1), pageCount || 1);
    setPageNumber(bounded);
    setPageInput(String(bounded));
  }, [pageCount]);

  const zoomOut = useCallback(() => {
    setFitWidth(false);
    setZoom((value) => Math.max(0.5, Number((value - 0.15).toFixed(2))));
  }, []);

  const zoomIn = useCallback(() => {
    setFitWidth(false);
    setZoom((value) => Math.min(2.5, Number((value + 0.15).toFixed(2))));
  }, []);

  const resetFitWidth = useCallback(() => {
    setFitWidth(true);
    setZoom(1);
  }, []);

  const scrollStage = useCallback((direction) => {
    const stage = stageRef.current;
    if (!stage) return false;
    const before = stage.scrollTop;
    const maxScroll = stage.scrollHeight - stage.clientHeight;
    const delta = Math.max(160, Math.floor(stage.clientHeight * 0.72)) * direction;
    stage.scrollBy({ top: delta, behavior: "smooth" });
    return direction > 0 ? before < maxScroll - 4 : before > 4;
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const node = stageRef.current?.parentElement;
    if (!node) return;
    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (error) {
      toast.error("Le mode plein ecran n'est pas disponible");
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      const tagName = event.target?.tagName?.toLowerCase();
      if (tagName === "input" || tagName === "textarea") return;
      if (event.key === "ArrowRight" || event.key === "PageDown") {
        event.preventDefault();
        goToPage(pageNumber + 1);
      }
      if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault();
        goToPage(pageNumber - 1);
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!scrollStage(1)) goToPage(pageNumber + 1);
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!scrollStage(-1)) goToPage(pageNumber - 1);
      }
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomIn();
      }
      if (event.key === "-") {
        event.preventDefault();
        zoomOut();
      }
      if (event.key.toLowerCase() === "w" || event.key === "0") {
        event.preventDefault();
        resetFitWidth();
      }
      if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        setRotation((value) => (value + 90) % 360);
      }
      if (event.key.toLowerCase() === "f") {
        event.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToPage, pageNumber, resetFitWidth, scrollStage, toggleFullscreen, zoomIn, zoomOut]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return undefined;
    let cancelled = false;
    setRendering(true);
    renderTaskRef.current?.cancel?.();

    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return null;
      const baseViewport = page.getViewport({ scale: 1, rotation });
      const liveStageWidth = getUsableStageWidth();
      if (Math.abs(liveStageWidth - stageWidth) > 8) {
        setStageWidth(liveStageWidth);
        return null;
      }
      const fitScale = Math.min(2.4, Math.max(0.2, liveStageWidth / baseViewport.width));
      const scale = fitWidth ? fitScale : fitScale * zoom;
      const viewport = page.getViewport({ scale, rotation });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d", { alpha: false });
      const pixelRatio = window.devicePixelRatio || 1;

      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, viewport.width, viewport.height);

      renderTaskRef.current = page.render({ canvasContext: context, viewport });
      return renderTaskRef.current.promise;
    }).then(() => {
      if (cancelled) return;
      setRendering(false);
      const percent = pageCount ? Math.min(100, (pageNumber / pageCount) * 100) : 0;
      onPageChange(pageNumber, percent);
    }).catch((error) => {
      if (error?.name !== "RenderingCancelledException") {
        console.error("PDF render error", error);
        toast.error("Erreur de rendu PDF");
      }
      setRendering(false);
    });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [fitWidth, getUsableStageWidth, onPageChange, pageCount, pageNumber, pdfDoc, rotation, stageWidth, zoom]);

  const submitPage = (event) => {
    event.preventDefault();
    const parsed = Number(pageInput);
    if (!Number.isNaN(parsed)) goToPage(parsed);
  };

  const progressPercent = pageCount ? Math.min(100, (pageNumber / pageCount) * 100) : 0;

  return (
    <div className="flex h-full min-h-[360px] flex-col bg-[#100D0A] fullscreen:h-screen">
      <div className="sticky top-0 z-10 border-b border-white/10 bg-[#100D0A]/95 px-3 py-2 backdrop-blur sm:px-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{item.title}</p>
            <p className="text-xs text-white/60">Page {pageNumber} sur {pageCount || "..."}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
            <div className="col-span-2 flex items-center gap-2 sm:col-span-1">
              <IconButton label="Page précédente" disabled={pageNumber <= 1 || rendering} onClick={() => goToPage(pageNumber - 1)}>
                <ChevronLeft size={17} />
              </IconButton>
              <form onSubmit={submitPage} className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-1">
                <input
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value.replace(/[^\d]/g, ""))}
                  className="h-7 w-12 bg-transparent text-center text-sm text-white outline-none"
                  inputMode="numeric"
                  aria-label="Page"
                />
                <span className="text-xs text-white/50">/ {pageCount || "-"}</span>
              </form>
              <IconButton label="Page suivante" disabled={!pageCount || pageNumber >= pageCount || rendering} onClick={() => goToPage(pageNumber + 1)}>
                <ChevronRight size={17} />
              </IconButton>
            </div>

            <div className="flex items-center gap-2">
              <IconButton label="Zoom arrière" onClick={zoomOut}>
                <ZoomOut size={17} />
              </IconButton>
              <span className="min-w-12 text-center text-xs text-white/70">{Math.round(zoom * 100)}%</span>
              <IconButton label="Zoom avant" onClick={zoomIn}>
                <ZoomIn size={17} />
              </IconButton>
            </div>

            <div className="flex items-center gap-2">
              <IconButton label="Ajuster à la largeur" active={fitWidth} onClick={resetFitWidth}>
                <Maximize2 size={17} />
              </IconButton>
              <IconButton label="Rotation" onClick={() => setRotation((value) => (value + 90) % 360)}>
                <RotateCw size={17} />
              </IconButton>
              <IconButton label={isFullscreen ? "Quitter plein ecran" : "Plein ecran"} active={isFullscreen} onClick={toggleFullscreen}>
                <Maximize2 size={17} />
              </IconButton>
            </div>
          </div>
        </div>
        <Progress value={progressPercent} className="mt-3 h-1 bg-white/10 [&>div]:bg-[#D4A373]" />
      </div>

      <div ref={stageRef} className="relative flex-1 overflow-auto overscroll-contain bg-[radial-gradient(circle_at_top,#2A2019,#100D0A_55%,#080605)] p-3 outline-none sm:p-6" tabIndex={0}>
        {rendering && (
          <div className="fixed left-1/2 top-24 z-20 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-sm text-white shadow-lg">
            Rendu PDF...
          </div>
        )}
        <div className="mx-auto flex min-h-full w-max max-w-full justify-center pb-10">
          <canvas ref={canvasRef} className="h-auto max-w-none rounded-md bg-white shadow-2xl" />
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#100D0A] px-3 py-2 text-xs text-white/55 sm:hidden">
        Balayez horizontalement si vous zoomez. Flèches clavier, +, -, W, R sur desktop.
      </div>
    </div>
  );
}

function IconButton({ children, label, disabled = false, active = false, onClick }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${
        active
          ? "border-[#D4A373] bg-[#D4A373] text-white"
          : "border-white/15 bg-white/5 text-white hover:bg-white/10"
      } disabled:cursor-not-allowed disabled:opacity-35`}
    >
      {children}
    </button>
  );
}
